import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { routes } from '~/api/routes';
import { useFetch } from '~/hooks/useFetch';
import { getRegistryActionMetadataSchemaResponseSchema, type Registries, registriesSchema } from '~/schemas/registries';
import type { RegistryTransactionType } from '~/schemas/registry-transactions';

export const registriesKeys = {
  getRegistries: () => [...registriesKeys.root, 'getRegistries'],
  getRegistryActionMetadataSchema: (registry: string, actionType: string, batchId: string | undefined) => [
    ...registriesKeys.root,
    'getRegistryActionMetadataSchema',
    { registry, actionType, batchId },
  ],
  root: ['registries'],
} as const;

interface useGetRegistriesParams<TSelected> {
  select?: (registries: Registries) => TSelected;
  enabled?: boolean;
}

export function useGetRegistries<TSelected = Registries>({
  select,
  enabled = true,
}: useGetRegistriesParams<TSelected> = {}) {
  const { fetchData } = useFetch();

  return useQuery({
    enabled,
    queryFn: () => fetchData(routes.registries.getRegistries, { response: registriesSchema }),
    queryKey: registriesKeys.getRegistries(),
    select,
    staleTime: 5 * 60 * 1000, // don't refetch for 5 mins
  });
}

export function useGetRegistry(identifier: string | undefined) {
  const selectRegistry = useCallback(
    (registries: Registries) => registries.find((registry) => registry.identifier === identifier),
    [identifier],
  );

  return useGetRegistries({ enabled: !!identifier, select: selectRegistry });
}

/**
 * Fetch the metadata JSON Schema (Pydantic `model_json_schema()`) for a (registry, action_type). The
 * response is `{}` when no metadata is required. Adapted into form fields by the metadata renderer.
 * When a `batchId` is supplied, the backend prefills fields it can derive from the batch as JSON
 * Schema `default`s (the operator can still edit them).
 */
export function useGetRegistryActionMetadataSchema({
  registry,
  actionType,
  batchId,
  enabled = true,
}: {
  registry: string | undefined;
  actionType: RegistryTransactionType | undefined;
  batchId?: string;
  enabled?: boolean;
}) {
  const { fetchData } = useFetch();

  return useQuery({
    enabled: enabled && !!registry && !!actionType,
    queryFn: () =>
      fetchData(
        routes.registries.getRegistryActionMetadataSchema(registry ?? '', actionType ?? ''),
        { response: getRegistryActionMetadataSchemaResponseSchema },
        { queryParams: batchId ? new URLSearchParams({ batch_id: batchId }) : undefined },
      ),
    queryKey: registriesKeys.getRegistryActionMetadataSchema(registry ?? '', actionType ?? '', batchId),
    staleTime: 5 * 60 * 1000,
  });
}
